/* global debounce */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      
      // Initialize free product validation
      this.initFreeProductValidation();
      
      if (this.hasChildNodes()) this.init();
    }

    initFreeProductValidation() {
      // Master freebies toggle — when off, skip all freebie validation
      const freebiesEnabled = theme.settings.cartProgressBar?.freebiesEnabled === true;
      const variantIdString = freebiesEnabled ? theme.settings.cartProgressBar?.freeProductVariantId : null;
      this.freeProductVariantId = variantIdString ? parseInt(variantIdString, 10) : null;

      // Only proceed if we have a free product configured and freebies are enabled
      if (!this.freeProductVariantId || this.freeProductVariantId === 0) {
        if (!window.__pfFreebieLoggedOnce) {
          window.__pfFreebieLoggedOnce = true;
          console.log('[ProductForm] Freebies disabled or no variant configured — freebie validation skipped for all product-forms on this page.');
        }
        return;
      }
      
      if (!window.__pfFreebieActiveLoggedOnce) {
        window.__pfFreebieActiveLoggedOnce = true;
        console.log('[ProductForm] Freebie validation active — variantId:', this.freeProductVariantId);
      }

      // Override the handleSubmit method to check for free product
      const originalHandleSubmit = this.handleSubmit;
      this.handleSubmit = async (event) => {
        const variantIdInput = this.querySelector('input[name="id"]');
        if (variantIdInput && variantIdInput.value === this.freeProductVariantId) {
          console.log('Product Form: Intercepted form submission trying to add free product');
          
          // Check if already in cart using fresh cart data
          const alreadyInCart = await this.checkIfFreeProductInCart();
          if (alreadyInCart) {
            console.log('Product Form: Free product already in cart, preventing form submission');
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
        }
        
        // Call original handler if not blocked
        return originalHandleSubmit.call(this, event);
      };
    }

    async checkIfFreeProductInCart() {
      try {
        // Fetch fresh cart JSON to check if free product line item exists
        const response = await fetch(`${theme.routes.cart}.js`);
        if (response.ok) {
          const cartData = await response.json();
          const freeProductItem = cartData.items.find(item => item.variant_id.toString() === this.freeProductVariantId);
          const hasFreeProduct = !!freeProductItem;
          console.log('Product Form: Free product line item in fresh cart data:', hasFreeProduct);
          if (freeProductItem) {
            console.log('Product Form: Free product found with quantity:', freeProductItem.quantity, 'at line index:', freeProductItem.index);
          }
          return hasFreeProduct;
        }
      } catch (error) {
        console.error('Product Form: Error checking free product in cart:', error);
      }
      
      // Fallback to DOM check if API fails
      const cartItems = document.querySelectorAll('.cart-item');
      for (let item of cartItems) {
        const variantId = item.dataset.variantId;
        if (variantId === this.freeProductVariantId) {
          return true;
        }
      }
      return false;
    }

    async triggerFreeProductCheck() {
      // Only trigger if we have a free product configured
      if (!this.freeProductVariantId || this.freeProductVariantId === '0') {
        return;
      }

      console.log('Product Form: Triggering free product check after cart add');
      
      // Dispatch a custom event that the cart progress bar can listen to
      document.dispatchEvent(new CustomEvent('on:free-product:check', {
        bubbles: true,
        detail: { 
          source: 'product-form',
          freeProductVariantId: this.freeProductVariantId 
        }
      }));

      // Also perform the actual free product milestone check
      try {
        // Fetch current cart data
        const response = await fetch(`${theme.routes.cart}.js`);
        if (response.ok) {
          const cartData = await response.json();
          console.log('Product Form: Fetched cart data for free product check:', cartData);
          
          // Handle free product milestone logic
          await this.handleFreeProductMilestone(cartData);
        } else {
          console.error('Product Form: Failed to fetch cart data for free product check');
        }
      } catch (error) {
        console.error('Product Form: Error during free product check:', error);
      }
    }

    async triggerFreeProductCheckWithData(cartData) {
      // Only trigger if we have a free product configured
      if (!this.freeProductVariantId || this.freeProductVariantId === '0') {
        return;
      }

      console.log('Product Form: Triggering free product check with provided cart data');

      // Delegate to cart-items when available so only ONE place adds the freebie (avoids duplicate when ATC is used)
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.checkAndHandleFreeProductImmediately === 'function') {
        try {
          const updated = await cartItems.checkAndHandleFreeProductImmediately(cartData);
          return updated || cartData;
        } catch (error) {
          console.error('Product Form: Error delegating free product check to cart-items:', error);
        }
      }

      // Fallback when cart-items not in DOM (e.g. drawer not rendered yet): use product-form add with global lock
      document.dispatchEvent(new CustomEvent('on:free-product:check', {
        bubbles: true,
        detail: { source: 'product-form', freeProductVariantId: this.freeProductVariantId }
      }));
      try {
        await this.handleFreeProductMilestone(cartData);
      } catch (error) {
        console.error('Product Form: Error during free product check with data:', error);
      }
    }

    /**
     * Improved removal function using line item keys
     * @param {number} variantId - The variant ID to remove
     * @returns {Promise<Object>} Updated cart data
     */
    async removeAllByVariantId(variantId) {
      console.log('Product Form: Removing all items with variant ID:', variantId);
      
      try {
        // 1) Get current cart
        const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
        console.log('Product Form: Current cart items:', cart.items.length);

        // 2) Find all line item keys for this variant
        const keysToRemove = cart.items
          .filter(item => item.variant_id === variantId)
          .map(item => item.key); // key uniquely identifies the line (variant + properties + selling plan, etc.)

        console.log('Product Form: Found items to remove:', keysToRemove.length);
        
        if (keysToRemove.length === 0) {
          console.log('Product Form: No items found to remove');
          return cart; // nothing to remove
        }

        // 3) Remove each matching line by key
        for (const key of keysToRemove) {
          console.log('Product Form: Removing line key:', key);
          
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 }) // using line-item key ensures exact match
          });
          
          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Failed to remove line key ${key}: ${errorText}`);
          }
          
          console.log('Product Form: Successfully removed line key:', key);
        }

        // 4) Return final cart
        const finalCart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
        console.log('Product Form: Final cart items:', finalCart.items.length);
        
        // Update cart bubble count directly - fetch fresh cart data to ensure accuracy
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          try {
            // Fetch fresh cart data to ensure we have the latest count
            const freshCartResponse = await fetch('/cart.js');
            const freshCartData = await freshCartResponse.json();
            
            const countElement = cartIconBubble.querySelector('.header__cart-count-number');
            const countText = cartIconBubble.querySelector('.visually-hidden');
            
            if (countElement) {
              countElement.textContent = freshCartData.item_count;
              console.log('Product Form: Cart bubble count updated to:', freshCartData.item_count, '(fresh data)');
            }
            
            if (countText) {
              const itemText = freshCartData.item_count === 1 ? 'item' : 'items';
              countText.textContent = `${freshCartData.item_count} ${itemText}`;
            }
            
            // Show/hide cart count based on whether cart is empty
            const cartCountContainer = cartIconBubble.querySelector('.header__cart-count');
            if (cartCountContainer) {
              if (freshCartData.item_count > 0) {
                cartCountContainer.style.display = '';
              } else {
                cartCountContainer.style.display = 'none';
              }
            }
          } catch (error) {
            console.error('Product Form: Error fetching fresh cart data for bubble update:', error);
            // Fallback to using finalCart if fresh fetch fails
            const countElement = cartIconBubble.querySelector('.header__cart-count-number');
            const countText = cartIconBubble.querySelector('.visually-hidden');
            
            if (countElement) {
              countElement.textContent = finalCart.item_count;
            }
            
            if (countText) {
              const itemText = finalCart.item_count === 1 ? 'item' : 'items';
              countText.textContent = `${finalCart.item_count} ${itemText}`;
            }
          }
        }
        
        return finalCart;
        
      } catch (error) {
        console.error('Product Form: Error removing items by variant ID:', error);
        throw error;
      }
    }

    /**
     * Calculate cart total excluding free product
     * @param {Object} cartData - Cart data
     * @returns {number} Total in cents excluding free product
     */
    calculateCartTotalExcludingFreeProduct(cartData) {
      // Get free product variant ID safely
      const freeProductVariantId = this.freeProductVariantId || (theme.settings.cartProgressBar?.freeProductVariantId ? parseInt(theme.settings.cartProgressBar.freeProductVariantId, 10) : null);
      
      return cartData.items
        .filter(item => {
          const isFreeProduct = freeProductVariantId ? item.variant_id === freeProductVariantId : false;
          const isMilestonePopupProduct = item.properties && item.properties._3rd_milestone_popup === 'true';
          return !isFreeProduct && !isMilestonePopupProduct;
        })
        .reduce((total, item) => total + (item.final_line_price), 0);
    }

    /**
     * Add free product to cart
     * @returns {Promise<Object>} Updated cart data
     */
    async addFreeProductToCart() {
      console.log('Product Form: Adding free product to cart');

      // Use same global lock as cart-items so we never add in parallel (prevents duplicate freebie when ATC triggers multiple code paths)
      if (!window.freeProductLock) {
        window.freeProductLock = { isProcessing: false, variantId: null };
      }
      if (window.freeProductLock.isProcessing) {
        console.log('Product Form: Global free product lock active, skipping add');
        return null;
      }
      window.freeProductLock.isProcessing = true;
      window.freeProductLock.variantId = this.freeProductVariantId;

      try {
        // CRITICAL: Check if free product is already in cart before adding
        console.log('Product Form: Performing final atomic check before adding free product');
        const freshCartCheck = await fetch(`${theme.routes.cart}.js`);
        if (!freshCartCheck.ok) {
          console.error('Product Form: Failed to fetch cart for final check, aborting addition');
          throw new Error('Failed to check cart status');
        }
        
        let freshCartData = await freshCartCheck.json();
        const existingFreeProductItem = freshCartData.items.find(item => 
          item.variant_id.toString() === this.freeProductVariantId.toString()
        );
        
        if (existingFreeProductItem) {
          // Check if it's already a freebie (has freebie properties)
          const isAlreadyFreebie = existingFreeProductItem.properties && 
            (existingFreeProductItem.properties._free_product === 'true' || 
             existingFreeProductItem.properties['_free_product'] === 'true');
          
          if (isAlreadyFreebie) {
            console.log('Product Form: ✅ FREE PRODUCT ALREADY EXISTS AS FREEBIE in cart! Skipping addition to prevent duplicate.');
            return freshCartData; // Return current cart data, don't add
          } else {
            // Variant exists but NOT as a freebie - remove it first, then add as freebie
            console.log('Product Form: Variant exists as normal product (not freebie). Removing it first, then adding as freebie.');
            console.log('Product Form: Existing item properties:', existingFreeProductItem.properties);
            const updatedCart = await this.removeAllByVariantId(this.freeProductVariantId);
            // Use the updated cart data for the rest of the function
            freshCartData = updatedCart;
          }
        }
        
        // Use the same approach as CartItems for consistency and UI updates
        const formData = new FormData();
        formData.append('id', this.freeProductVariantId);
        formData.append('quantity', '1');
        
        // Add Kite promo properties for first freebie
        formData.append('properties[_free_product]', 'true');
        formData.append('properties[_rule_id]', 'SPEND_X_0');
        formData.append('properties[_kite_promo_name]', 'Freebie 1');
        
        formData.append('sections', 'cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);
        
        // Add cart drawer section if it exists
        if (this.cartDrawer) {
          const cartDrawerSection = this.cartDrawer.closest('.shopify-section');
          if (cartDrawerSection) {
            formData.append('sections', cartDrawerSection.id.replace('shopify-section-', ''));
          }
        }
        
        console.log('Product Form: Making API call to add free product');
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Product Form: ✅ SUCCESS! Free product added to cart');
          
          // Update cart icon count using the response data
          if (data.sections && data.sections['cart-icon-bubble']) {
            const cartIconBubble = document.getElementById('cart-icon-bubble');
            if (cartIconBubble) {
              cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
            }
          }
          
          // Update cart drawer if it exists
          if (this.cartDrawer) {
            this.cartDrawer.refreshCartDrawer();
          }
          
          if (typeof window.ccDispatchCartChange === 'function') {
            window.ccDispatchCartChange({
              cart: data,
              newTotalQuantity: data.item_count,
              newTotalPrice: data.total_price
            });
          }

          return data;
        } else {
          const errorData = await response.text();
          throw new Error(`Failed to add free product: ${errorData}`);
        }
        
      } catch (error) {
        console.error('Product Form: Error adding free product:', error);
        throw error;
      } finally {
        if (window.freeProductLock) {
          window.freeProductLock.isProcessing = false;
          window.freeProductLock.variantId = null;
        }
      }
    }

    /**
     * Handle free product milestone logic
     * @param {Object} cartData - Current cart data
     */
    async handleFreeProductMilestone(cartData) {
      console.log('Product Form: ===== HANDLING FREE PRODUCT MILESTONE =====');
      console.log('Product Form: Cart data:', cartData);
      
      // Calculate cart total excluding free product
      const cartTotalExcludingFreeProduct = this.calculateCartTotalExcludingFreeProduct(cartData);
      console.log('Product Form: Cart total excluding free product:', cartTotalExcludingFreeProduct, 'cents = $', (cartTotalExcludingFreeProduct / 100));
      
      // Get free product variant ID (may not be set on all product forms)
      const freeProductVariantId = this.freeProductVariantId || (theme.settings.cartProgressBar?.freeProductVariantId ? parseInt(theme.settings.cartProgressBar.freeProductVariantId, 10) : null);
      
      // Check if free product is currently in cart (only if variant ID is set)
      const freeProductItem = freeProductVariantId ? cartData.items.find(item => item.variant_id === freeProductVariantId) : null;
      let hasFreeProduct = !!freeProductItem;
      
      console.log('Product Form: Has free product in cart:', hasFreeProduct);
      if (freeProductItem) {
        console.log('Product Form: Free product details:', freeProductItem);
      }
      
      // Check for 3rd milestone popup products to remove
      const milestone3PopupItems = cartData.items.filter(item => 
        item.properties && item.properties._3rd_milestone_popup === 'true'
      );
      
      console.log('Product Form: Found', milestone3PopupItems.length, '3rd milestone popup products in cart');
      
      // Get milestone thresholds from global settings (in dollars, convert to cents)
      const milestone2Threshold = (theme.settings.cartProgressBar?.milestone2 || 10) * 100; // Default $10
      const milestone3Threshold = (theme.settings.cartProgressBar?.milestone3 || 15) * 100; // Default $15
      
      console.log('🎯 Product Form: ===== FREEBIE THRESHOLDS =====');
      console.log('🎯 Product Form: Milestone 2 (Freebie 1) threshold:', milestone2Threshold, 'cents = $', (milestone2Threshold / 100));
      console.log('🎯 Product Form: Milestone 3 (Freebie 2) threshold:', milestone3Threshold, 'cents = $', (milestone3Threshold / 100));
      console.log('🎯 Product Form: Cart total (excluding freebie):', cartTotalExcludingFreeProduct, 'cents = $', (cartTotalExcludingFreeProduct / 100));
      
      // Handle removals: Check if both freebies need to be removed (below both thresholds)
      let updatedCartData = cartData;
      let needsFreebie1Removal = false;
      
      // Check if both need to be removed - do them together for efficiency
      const needsMilestone3Removal = cartTotalExcludingFreeProduct < milestone3Threshold && milestone3PopupItems.length > 0;
      needsFreebie1Removal = cartTotalExcludingFreeProduct < milestone2Threshold && hasFreeProduct;
      
      // Remove 3rd milestone popup products if below threshold
      if (needsMilestone3Removal) {
        console.log('Product Form: 🚨 MILESTONE 3 BELOW THRESHOLD! Removing', milestone3PopupItems.length, 'popup products');
        updatedCartData = await this.removeMilestone3PopupProducts(cartData, milestone3PopupItems);
        // Update hasFreeProduct from the returned cart data (no need to re-fetch)
        if (updatedCartData && updatedCartData.items) {
          const freebie1AfterRemoval = updatedCartData.items.find(item => item.variant_id === this.freeProductVariantId);
          hasFreeProduct = !!freebie1AfterRemoval;
          needsFreebie1Removal = cartTotalExcludingFreeProduct < milestone2Threshold && hasFreeProduct;
          console.log('Product Form: After milestone 3 removal, freebie 1 still in cart:', hasFreeProduct);
        }
      }
      
      // Milestone logic: Add free product at milestone2 threshold, remove below threshold
      // Check this AFTER milestone 3 removal to ensure both are handled
      if (cartTotalExcludingFreeProduct >= milestone2Threshold) {
        // Should have free product
        if (!hasFreeProduct) {
          console.log('Product Form: 🎉 MILESTONE $' + (milestone2Threshold / 100) + '+ REACHED! Adding free product');
          
          try {
            const freeProductResult = await this.addFreeProductToCart();
            
            // Additional UI updates after free product is added
            if (freeProductResult) {
              // Update cart page if we're on the cart page
              if (window.location.pathname === theme.routes.cart) {
                const cartItems = document.querySelector('cart-items');
                if (cartItems) {
                  cartItems.refreshCartItems();
                }
              }
              
              if (typeof window.ccDispatchCartChange === 'function') {
                window.ccDispatchCartChange({
                  cart: freeProductResult,
                  newTotalQuantity: freeProductResult.item_count,
                  newTotalPrice: freeProductResult.total_price
                });
              }
            }
          } catch (error) {
            // If the error is because free product already exists, that's fine
            // Just log it and continue - the product was likely added by another process
            if (error.message && error.message.includes('FREE PRODUCT ALREADY EXISTS')) {
              console.log('Product Form: ℹ️ Free product already exists, continuing gracefully');
            } else {
              // Log other errors but don't block the UI
              console.error('Product Form: Error adding free product (non-fatal):', error);
            }
          }
        } else {
          console.log('Product Form: ✅ MILESTONE $' + (milestone2Threshold / 100) + '+ REACHED! Free product already exists');
        }
      } else {
          // Should not have free product - remove if it exists
          if (needsFreebie1Removal || hasFreeProduct) {
            console.log('Product Form: 🚨 MILESTONE BELOW $' + (milestone2Threshold / 100) + '! Removing freebie 1');
            
            // Remove freebie 1 using the updated cart data (after milestone 3 removal if it happened)
          await this.removeAllByVariantId(this.freeProductVariantId);
            // Return the updated cart data directly - no need for another fetch
            return updatedCartData;
        } else {
            console.log('Product Form: ✅ MILESTONE BELOW $' + (milestone2Threshold / 100) + '! No free product to remove');
        }
      }
    }

    async removeMilestone3PopupProducts(cartData, popupItems) {
      try {
        console.log('Product Form: ===== REMOVING 3RD MILESTONE POPUP PRODUCTS =====');
        console.log('Product Form: Found', popupItems.length, 'popup products to remove');
        
        // Remove all popup products in parallel for speed (using Promise.all)
        const removalPromises = popupItems.map(async (item) => {
          try {
          console.log('Product Form: Removing popup product:', item.title, 'Key:', item.key);
          
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });

            if (!response.ok) {
              const errorText = await response.text();
              // If 400/404, item was already removed - that's okay
              if (response.status === 400 || response.status === 404) {
                console.log('Product Form: Item already removed (status ' + response.status + '), continuing:', item.title);
                return null;
              }
              throw new Error(`HTTP error! status: ${response.status}, ${errorText}`);
            }

            const result = await response.json();
            console.log('Product Form: Successfully removed popup product:', item.title);
            return result;
            
          } catch (error) {
            console.error('Product Form: Error removing popup product:', item.title, error);
            return null; // Return null on error, we'll continue
          }
        });
        
        // Wait for all removals to complete
        await Promise.all(removalPromises);
        
        console.log('Product Form: ✅ SUCCESS! All 3rd milestone popup products removed');
        
        // Clear session flags to allow popup to show again
        sessionStorage.removeItem('milestone3-popup-product-selected');
        sessionStorage.removeItem('milestone3-popup-shown');
        console.log('Product Form: Cleared popup session flags - popup can show again');
        
        // Fetch fresh cart data after removal to get accurate count
        const freshCartResponse = await fetch('/cart.js');
        const freshCartData = await freshCartResponse.json();
        
        // Update cart bubble count
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const countElement = cartIconBubble.querySelector('.header__cart-count-number');
          const countText = cartIconBubble.querySelector('.visually-hidden');
          
          if (countElement) {
            countElement.textContent = freshCartData.item_count;
            console.log('Product Form: Cart bubble count updated to:', freshCartData.item_count, 'after removing popup products');
          }
          
          if (countText) {
            const itemText = freshCartData.item_count === 1 ? 'item' : 'items';
            countText.textContent = `${freshCartData.item_count} ${itemText}`;
          }
          
          // Show/hide cart count based on whether cart is empty
          const cartCountContainer = cartIconBubble.querySelector('.header__cart-count');
          if (cartCountContainer) {
            if (freshCartData.item_count > 0) {
              cartCountContainer.style.display = '';
            } else {
              cartCountContainer.style.display = 'none';
            }
          }
        }
        
        // Trigger UI updates - refresh cart items on cart page
        if (window.location.pathname === theme.routes.cart) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems) {
            console.log('Product Form: Refreshing cart items on cart page after popup product removal');
            cartItems.refreshCartItems();
          }
        }
        
        // Update cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.refreshCartDrawer) {
          console.log('Product Form: Refreshing cart drawer after popup product removal');
          cartDrawer.refreshCartDrawer();
        }
        
        if (typeof window.ccDispatchCartChange === 'function') {
          window.ccDispatchCartChange({
            source: 'milestone3_removal',
            action: 'popup_products_removed',
            removedItems: popupItems.length,
            cart: updatedCart || cartData
          });
        }
        
      } catch (error) {
        console.error('Product Form: Error removing 3rd milestone popup products:', error);
      }
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));

        if (this.dataset.showQuantitySelector) {
          this.quantitySelector = this.querySelector('quantity-input');

          if (this.quantitySelector) {
            this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
            this.quantitySelector.addEventListener('change', debounce(this.handleChange.bind(this)));
            this.lineItemChangeListener = this.lineItemChangeListener
              || this.handleLineItemChange.bind(this);
            document.addEventListener('on:line-item:change', (evt) => {
              this.lineItemChangeListener(evt);
            });
          }
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
        document.addEventListener('on:variant:change', this.setMaximumInputQuantity.bind(this));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('on:line-item:change', this.lineItemChangeListener);
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          if (dataEl) {
            const variantId = Number(this.form.querySelector('[name="id"]').value);
            const inventoryJson = JSON.parse(dataEl.textContent);
            const inventory = inventoryJson.find((item) => item.id === variantId);

            if (inventory.inventory_management.length !== 0 && inventory.inventory_quantity > 0 && inventory.inventory_policy !== 'continue') {
              quantityInput.max = inventory.inventory_quantity;

              const currentQuantity = Number(quantityInput.value);
              if (currentQuantity > 1 && currentQuantity > inventory.inventory_quantity) {
                quantityInput.value = quantityInput.max;
              }
            }
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      // Enable or disable buttons based on the newQuantity relative to max and min
      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    /**
     * Handles 'change' events on the line item (dispatched by the theme).
     * @param {object} evt - Event object.
     */
    handleLineItemChange(evt) {
      if (evt.detail.variantId.toString() === this.dataset.variantId) {
        debounce(this.updateQuantityState(evt.detail.newQuantity));
      }
    }

    /**
     * Handles 'change' events on the quantity-input element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.value);
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      const atcDbg = (step, data) => {
        if (window.CCFlareDebug && window.CCFlareDebug.enabled()) {
          window.CCFlareDebug.log('atc', 'product-form.handleSubmit', step, data);
        }
      };
      const atcTime = (id, step, data) => {
        if (window.CCFlareDebug && window.CCFlareDebug.enabled()) {
          window.CCFlareDebug.timeStart('atc:' + id, 'atc', 'product-form.' + step, data);
        }
      };
      const atcTimeEnd = (id, step, data) => {
        if (window.CCFlareDebug && window.CCFlareDebug.enabled()) {
          window.CCFlareDebug.timeEnd('atc:' + id, 'atc', 'product-form.' + step, data);
        }
      };

      atcTime('total', 'handleSubmit', { variantId: this.form.querySelector('[name="id"]')?.value });
      atcDbg('start', { pathname: window.location.pathname });

      // Always-on ATC timing log — readable in any browser console
      window.__atcT0 = performance.now();
      const _atcLog = (step, extra) => {
        const ms = (performance.now() - window.__atcT0).toFixed(0);
        const suffix = extra ? ' ' + JSON.stringify(extra) : '';
        console.log(`[ATC +${ms}ms] ${step}${suffix}`);
      };
      _atcLog('🚀 START', { variant: this.form.querySelector('[name="id"]')?.value });

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      // Check for quantity limit before proceeding
      const maxQuantity = this.dataset.maxQuantity ? parseInt(this.dataset.maxQuantity, 10) : null;
      if (maxQuantity) {
        const quantityInput = this.form.querySelector('[name="quantity"]');
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
        const variantId = this.form.querySelector('[name="id"]').value;
        
        // Get current cart quantity for this variant
        try {
          atcTime('qty-check', 'quantityLimitCartFetch');
          _atcLog('📥 qty-limit check fetch START');
          const cartResponse = await fetch(`${theme.routes.cart}.js`);
          const cartData = await cartResponse.json();
          atcTimeEnd('qty-check', 'quantityLimitCartFetch', { item_count: cartData.item_count });
          _atcLog('📥 qty-limit check fetch DONE', { items: cartData.item_count });
          const cartItem = cartData.items.find(item => item.variant_id.toString() === variantId.toString());
          const currentCartQty = cartItem ? cartItem.quantity : 0;
          const newTotal = currentCartQty + quantity;
          
          if (newTotal > maxQuantity) {
            // Show the quantity limit modal
            if (window.quantityLimitManager) {
              window.quantityLimitManager.showModal(`You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`);
            } else {
              alert(`You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`);
            }
            return; // Stop the add to cart
          }
        } catch (error) {
          console.error('Error checking quantity limit:', error);
        }
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      _atcLog('🔘 button disabled, is-loading added');
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      // Enable cart loading states if on cart page
      if (window.location.pathname === theme.routes.cart) {
        this.enableCartLoading();
      }

      _atcLog('📋 building FormData + sections string');
      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Get all quick order lists on the page
      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);
      _atcLog('📋 FormData ready', { sections, variant: formData.get('id'), qty: formData.get('quantity') });

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let buttonReleased = false;

      try {
        const requestedFormQty = parseInt(formData.get('quantity') || '1', 10);

        atcTime('cart-add', 'cartAdd');
        _atcLog('🛒 /cart/add POST START');
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        _atcLog('🛒 /cart/add POST response received', { status: response.status, ok: response.ok });
        _atcLog('📄 parsing POST response JSON START');
        const data = await response.json();
        atcTimeEnd('cart-add', 'cartAdd', { ok: response.ok, status: response.status, variant_id: data.variant_id });
        _atcLog('📄 parsing POST response JSON DONE', { hasSections: !!(data.sections), sectionKeys: data.sections ? Object.keys(data.sections) : [], variant: data.variant_id, qty: data.quantity });
        atcDbg('cartAdd response', { hasSections: !!data.sections, quantity: data.quantity });
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          _atcLog('🔵 updateCartIcon START');
          ProductForm.updateCartIcon(data);
          _atcLog('🔵 updateCartIcon DONE');

          // ✅ Release the button and switch to qty selector immediately —
          // product is confirmed in cart, data.quantity is from the POST response.
          const newQuantity = data.quantity;
          const wasAlreadyInCart = data.quantity > requestedFormQty;

          _atcLog('🔘 button released (early — post success)');
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.add('is-success');
          this.submitBtn.removeAttribute('aria-disabled');
          setTimeout(() => { this.submitBtn.classList.remove('is-success'); }, 1000);
          buttonReleased = true;

          // Switch to quantity selector immediately — no need to wait for /cart.js.
          if (this.quantitySelector) {
            _atcLog('🔢 qty selector shown (early)');
            this.setAttribute('data-show-quantity-selector', 'true');
            this.quantityInput.value = newQuantity;
            this.quantitySelector.currentQty = this.quantityInput.dataset.initialValue;
          }

          // Sync all other product-form instances for the same variant (carousel clones / duplicate sections).
          const syncVariantId = this.dataset.variantId;
          if (syncVariantId) {
            document.querySelectorAll(`product-form[data-variant-id="${syncVariantId}"][data-show-quantity-selector]`).forEach(otherPf => {
              if (otherPf === this) return;
              const otherQtyInput = otherPf.querySelector('quantity-input .qty-input__input');
              if (otherQtyInput) {
                otherPf.setAttribute('data-show-quantity-selector', 'true');
                otherQtyInput.value = newQuantity;
              }
            });
          }

          // Update cart page title count if on cart page
          if (window.location.pathname === theme.routes.cart) {
            this.updateCartPageCount();
          }

          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          const openDrawer = !quickAddDrawer && this.cartDrawer && (
            (theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md)
            || theme.settings.afterAtc === 'drawer'
          );

          // Decide whether we need a /cart.js response.
          // We need it when: freebie check is active, OR something listens to ccPublishCartSnapshot.
          // When afterAtc="nothing" and freebies are off we skip the await and dispatch
          // immediately from the POST data — saves ~700ms of dead wait after drawer render.
          const freebiesActive = theme.settings.cartProgressBar?.freebiesEnabled === true;
          const needsCartJs = freebiesActive || !!window.ccPublishCartSnapshot;
          _atcLog('ℹ️ needsCartJs', { needsCartJs, freebiesActive, afterAtc: theme.settings.afterAtc });

          // Fire /cart.js in parallel — runs while we render the drawer below.
          // POST response already has the sections HTML so we don't need a second sections fetch.
          _atcLog('📥 /cart.js fetch START (parallel)');
          const newCartPromise = fetch(`${theme.routes.cart}.js`).then(r => r.json());

          if (this.cartDrawer) {
            // Render drawer directly from POST sections — no extra network round-trip.
            atcTime('drawer-render', 'renderContents');
            _atcLog('🗂️ renderContents START');
            await this.cartDrawer.renderContents(data, !!openDrawer);
            atcTimeEnd('drawer-render', 'renderContents');
            _atcLog('🗂️ renderContents DONE');
            if (typeof CartDrawer !== 'undefined' && CartDrawer.recalculateCssVarHeights) {
              CartDrawer.recalculateCssVarHeights();
            }
          }

          // Build a lightweight cart-like object from the POST response for events/freebie
          // when we skip awaiting the full /cart.js.
          const postCartData = {
            item_count: data.item_count,
            total_price: data.total_price,
            items: data.items || []
          };

          let newCartData;
          if (needsCartJs) {
            // Await the parallel /cart.js (typically already resolved by now).
            newCartData = await newCartPromise;
            _atcLog('📥 /cart.js fetch DONE (awaited)', { items: newCartData.item_count });
            if (window.ccPublishCartSnapshot) {
              window.ccPublishCartSnapshot(newCartData, 'product-form-atc');
            }
          } else {
            // Skip the await — use POST data for events, let /cart.js resolve in background.
            newCartData = postCartData;
            newCartPromise.then(d => {
              _atcLog('📥 /cart.js fetch DONE (background, not awaited)', { items: d.item_count });
            });
            _atcLog('📥 /cart.js NOT awaited — using POST data for events');
          }

          if (wasAlreadyInCart) {
            this.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id,
                oldQuantity: data.quantity - requestedFormQty,
                newQuantity
              }
            }));
          } else {
            atcDbg('dispatch on:cart:add', {
              variantId: data.variant_id,
              item_count: newCartData.item_count
            });
            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id
              }
            }));

            let freebieAdded = false;
            try {
              atcTime('free-product-check', 'triggerFreeProductCheckWithData');
              _atcLog('🎁 freebie check START');
              const afterFreebie = await this.triggerFreeProductCheckWithData(newCartData);
              atcTimeEnd('free-product-check', 'triggerFreeProductCheckWithData');
              _atcLog('🎁 freebie check DONE');
              if (afterFreebie && afterFreebie !== newCartData) {
                newCartData = afterFreebie;
                freebieAdded = true;
              }
            } catch (error) {
              console.error('Product Form: Error during free product check:', error);
            }

            // Only do a follow-up drawer refresh if the freebie check actually added something
            // (rare case — predictive freebie handles the common case at click time).
            if (freebieAdded && this.cartDrawer) {
              _atcLog('🗂️ freebie follow-up refresh START');
              await this.cartDrawer.refreshCartDrawer();
              _atcLog('🗂️ freebie follow-up refresh DONE');
            }
          }

          atcDbg('post-render', { hasCartDrawer: !!this.cartDrawer });
          if (this.cartDrawer) {
            if (quickOrderSections.length) {
              quickOrderSections.forEach((quickOrderSection) => {
                const sectionEl = document.getElementById(quickOrderSection.id);
                if (!sectionEl) return;
                const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                el.innerHTML = ProductForm.getElementHTML(
                  data.sections[quickOrderSection.section],
                  quickOrderSection.selector
                );
              });
            }
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                await cartItems.refreshCartItems();
              }
            }
          }

          if (typeof window.ccDispatchCartChange === 'function') {
            window.ccDispatchCartChange({
              cart: newCartData,
              newTotalQuantity: newCartData.item_count,
              newTotalPrice: newCartData.total_price,
              source: 'product-form-atc'
            });
          }
          _atcLog('✅ ATC COMPLETE — cart updated and UI refreshed');
        }
      } catch (error) {
        atcDbg('error', { error: String(error) });
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } finally {
        atcTimeEnd('total', 'handleSubmit', { done: true });
        _atcLog('🏁 TOTAL DONE', { buttonReleased });

        // Disable cart loading states if on cart page
        if (window.location.pathname === theme.routes.cart) {
          this.disableCartLoading();
        }

        // Only re-enable the button here on the error path.
        // The success path already released it immediately after POST.
        if (!buttonReleased) {
          _atcLog('🔘 button released (error path)');
          this.submitBtn.classList.add('is-success');
          this.submitBtn.removeAttribute('aria-disabled');
          setTimeout(() => {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.classList.remove('is-success');
          }, 1400);
        }
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    updateQuantityState(newQuantity) {
      this.quantityInput.value = newQuantity;
      this.quantitySelector.currentQty = newQuantity;
      this.setAttribute('data-show-quantity-selector', newQuantity > 0 ? 'true' : 'false');
    }

    async updateQuantity(quantity) {
      // Check for quantity limit before proceeding
      const maxQuantity = this.dataset.maxQuantity ? parseInt(this.dataset.maxQuantity, 10) : null;
      const requestedQuantity = parseInt(quantity, 10);
      
      if (maxQuantity && requestedQuantity > maxQuantity) {
        // Show the quantity limit modal
        if (window.quantityLimitManager) {
          window.quantityLimitManager.showModal(`You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`);
        } else {
          alert(`You can only buy ${maxQuantity} ${maxQuantity === 1 ? 'unit' : 'units'} of this item.`);
        }
        
        // Reset to max allowed or previous value
        const oldQuantity = parseInt(this.quantityInput.dataset.initialValue, 10);
        const cappedQuantity = Math.min(oldQuantity, maxQuantity);
        this.updateQuantityState(cappedQuantity);
        return; // Stop the quantity update
      }

      this.quantitySelector.setAttribute('aria-disabled', 'true');
      const lineErrors = this.querySelector('.js-product-form-errors');

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      // Request sections so we can update the header cart bubble in place
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        const drawerSection = this.cartDrawer.closest('.shopify-section');
        if (drawerSection) sections += `,${drawerSection.id.replace('shopify-section-', '')}`;
      }

      fetchRequestOpts.body = JSON.stringify({
        id: this.dataset.variantId,
        quantity,
        sections,
        sections_url: window.location.pathname
      });

      try {
        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        // Update cart icon bubble when quantity changes (including removals)
        ProductForm.updateCartIcon(data);

        // Route freebie check through cart-items when available (single code path)
        const cartItemsEl = document.querySelector('cart-items');
        if (cartItemsEl && typeof cartItemsEl.checkAndHandleFreeProductImmediately === 'function') {
          await cartItemsEl.checkAndHandleFreeProductImmediately(data);
        } else {
          await this.handleFreeProductMilestone(data);
        }
        
        // Also trigger cart progress bar check for milestone 3 popup
        document.dispatchEvent(new CustomEvent('on:free-product:check', {
          bubbles: true,
          detail: {
            source: 'product-form-quantity-change',
            cartData: data
          }
        }));

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        const oldQuantity = parseInt(this.quantityInput.dataset.initialValue, 10);
        this.updateQuantityState(oldQuantity);
      } finally {
        this.updateQuantityState(parseInt(quantity, 10));
        this.quantitySelector.removeAttribute('aria-disabled');
        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }

    /**
     * Enables cart loading states (shows spinners on cart items)
     */
    enableCartLoading() {
      // Enable loading on cart drawer if it exists
      if (this.cartDrawer) {
        this.cartDrawer.classList.add('pointer-events-none');
        this.cartDrawer.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = false;
        });
      }
      
      // Enable loading on cart items if on cart page
      if (window.location.pathname === theme.routes.cart) {
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
          cartItems.classList.add('pointer-events-none');
          cartItems.querySelectorAll('.cart-item__loader').forEach((loader) => {
            loader.hidden = false;
          });
        }
      }
    }

    /**
     * Disables cart loading states (hides spinners on cart items)
     */
    disableCartLoading() {
      // Disable loading on cart drawer if it exists
      if (this.cartDrawer) {
        this.cartDrawer.classList.remove('pointer-events-none');
        this.cartDrawer.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });
      }
      
      // Disable loading on cart items if on cart page
      if (window.location.pathname === theme.routes.cart) {
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
          cartItems.classList.remove('pointer-events-none');
          cartItems.querySelectorAll('.cart-item__loader').forEach((loader) => {
            loader.hidden = true;
          });
        }
      }
    }

    /**
     * Updates the cart count in the cart page title
     */
    updateCartPageCount() {
      const cartTitle = document.querySelector('.js-cart-title');
      if (cartTitle) {
        let cartCount = cartTitle.querySelector('.cart__count');
        if (!cartCount) {
          // Create cart count element if it doesn't exist
          cartCount = document.createElement('span');
          cartCount.className = 'cart__count';
          cartTitle.appendChild(document.createTextNode(' '));
          cartTitle.appendChild(cartCount);
        }
        
        // Get current cart item count from cart data
        fetch(`${theme.routes.cart}.js`)
          .then(response => response.json())
          .then(cartData => {
            if (cartData.item_count > 0) {
              cartCount.textContent = `(${cartData.item_count})`;
            } else {
              cartCount.remove();
              // Remove the space before the count as well
              const lastNode = cartTitle.lastChild;
              if (lastNode && lastNode.nodeType === Node.TEXT_NODE && lastNode.textContent === ' ') {
                lastNode.remove();
              }
            }
            
            if (typeof window.ccDispatchCartChange === 'function') {
              window.ccDispatchCartChange({
                cart: cartData,
                newTotalQuantity: cartData.item_count,
                newTotalPrice: cartData.total_price
              });
            }
          })
          .catch(error => {
            console.error('Error updating cart count:', error);
          });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}

